import React from 'react'
import { Alert, Button, Flex, Form, Input, InputNumber, Popconfirm } from 'antd'
import TextArea from 'antd/es/input/TextArea'
import { DeleteOutlined } from '@ant-design/icons'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { isNumber } from 'lodash'
import rubricSelectors from '../../../../../../store/selectors/rubric.selectors'
import {
    useFetchRubricsQuery,
    useCreateRubricMutation,
    useUpdateRubricMutation,
    useDeleteRubricMutation,
} from '../../../../../../store/apis/rubric.api'
import { fetchRubricsSelectors } from '../../../../../../store/apis/utils/selectors/rubric.api.selectors'
import { openNotificationWithIcon } from '../../../../../../utils/notification.helper'
import Access, { useAssignmentAccess } from '../../../../components/access/Access'
import { TextContentRules } from '../../../../components/access/access-rules/assignment-rules'
import Text from 'antd/es/typography/Text'
import { Perm, State } from '../../../../../../constants/assignment-permissions'
import MarkdownRenderer from '../../../../../../components/common/markdown-renderer'

const RubricPropertiesTab = ({ onClose }) => {
    const [form] = Form.useForm()
    const { token } = useParams()
    const editingRubricId = useSelector(rubricSelectors.editingRubricId)
    const isEditing = isNumber(editingRubricId)

    const { data: rubric } = useFetchRubricsQuery(
        { token },
        {
            skip: !isEditing,
            selectFromResult: result => fetchRubricsSelectors.byId({ id: editingRubricId }, result),
        },
    )

    const { hasAccess: hasDeletePermission, errorMessage: deleteErrorMessage } = useAssignmentAccess([
        Perm.CAN_EDIT,
        State.NOT_PROCESSING,
        State.NOT_MBA,
        State.UNPUBLISHED,
        State.LATEST,
        State.NO_SUBMISSIONS,
    ])

    const [createRubric, createResult] = useCreateRubricMutation()
    const [updateRubric, updateResult] = useUpdateRubricMutation()
    const [deleteRubric, deleteResult] = useDeleteRubricMutation()

    const isLoading = createResult.isLoading || updateResult.isLoading
    const error = createResult.error || updateResult.error

    const handleSubmit = async values => {
        if (isEditing) {
            const response = await updateRubric({
                rubricId: editingRubricId,
                name: values.name,
                content: values.content,
                gradingReferenceContent: values.gradingReferenceContent,
                weight: values.weight,
            })
            if ('data' in response) {
                openNotificationWithIcon('success', 'Rubric updated successfully')
            }
        } else {
            const response = await createRubric({
                token,
                name: values.name,
                content: values.content,
                gradingReferenceContent: values.gradingReferenceContent,
                weight: values.weight,
            })
            if ('data' in response) {
                openNotificationWithIcon('success', 'Rubric created successfully')
                onClose()
            }
        }
    }

    const handleDelete = async () => {
        const response = await deleteRubric({ rubricId: editingRubricId })
        if ('data' in response) {
            openNotificationWithIcon('success', 'Rubric deleted successfully')
            onClose()
        } else {
            openNotificationWithIcon('error', 'Failed to delete rubric')
        }
    }

    return (
        <Form form={form} layout='vertical' onFinish={handleSubmit} requiredMark={false}>
            <Form.Item
                name='name'
                label='Name'
                rules={[{ required: true, message: 'Please enter a rubric name' }]}
                initialValue={rubric?.name}
            >
                <Access
                    rules={TextContentRules.EDIT_NAMINGS}
                    fallback={<Text>{rubric?.name || 'No name provided'}</Text>}
                >
                    <Input placeholder='Enter rubric name' maxLength={255} showCount data-test='rubric-name-input' />
                </Access>
            </Form.Item>

            <Form.Item
                name='content'
                label='Content'
                tooltip='User-facing description shown to instructors and students'
                initialValue={rubric?.content}
            >
                <Access
                    rules={TextContentRules.EDIT_GRADINGS}
                    fallback={<Text>{rubric?.content || 'No content provided'}</Text>}
                >
                    <TextArea
                        placeholder='Enter content visible to users'
                        rows={4}
                        maxLength={15000}
                        showCount
                        data-test='rubric-content-input'
                    />
                </Access>
            </Form.Item>

            <Form.Item
                name='gradingReferenceContent'
                label='AI Grading Reference'
                tooltip='AI grading reference used in LLM prompts (not shown to students). Markdown is supported: # Heading, **Bold text**, - List item, 1. Numbered item'
                initialValue={rubric?.gradingReferenceContent}
            >
                <Access
                    rules={TextContentRules.EDIT_GRADINGS}
                    fallback={
                        <MarkdownRenderer
                            content={rubric?.gradingReferenceContent || 'No grading reference provided'}
                        />
                    }
                >
                    <TextArea
                        placeholder='Enter AI grading instructions'
                        rows={4}
                        maxLength={15000}
                        showCount
                        data-test='rubric-grading-reference-input'
                    />
                </Access>
            </Form.Item>

            <Form.Item name='weight' label='Weight' rules={[{ type: 'number', min: 0 }]} initialValue={rubric?.weight}>
                <Access
                    rules={TextContentRules.EDIT_GRADINGS}
                    fallback={<Text>{rubric?.weight || 'No weight provided'}</Text>}
                >
                    <InputNumber
                        placeholder='Enter weight (points)'
                        style={{ width: '100%' }}
                        min={0}
                        data-test='rubric-weight-input'
                    />
                </Access>
            </Form.Item>

            {error && (
                <Alert
                    message={error?.data?.detail || error?.message || 'An error occurred'}
                    type='error'
                    showIcon
                    closable
                    style={{ marginBottom: 16 }}
                />
            )}

            <Form.Item>
                <Flex justify='space-between'>
                    {isEditing && (
                        <Popconfirm
                            title='Delete Rubric'
                            description='Are you sure you want to delete this rubric? This will also remove all its categories.'
                            onConfirm={handleDelete}
                            okText='Delete'
                            okButtonProps={{ danger: true, loading: deleteResult.isLoading }}
                            cancelText='Cancel'
                        >
                            <Access rules={TextContentRules.EDIT_GRADINGS}>
                                <Button
                                    danger
                                    icon={<DeleteOutlined />}
                                    loading={deleteResult.isLoading}
                                    data-test='rubric-delete-button'
                                    disabled={!hasDeletePermission}
                                    title={deleteErrorMessage}
                                >
                                    Delete
                                </Button>
                            </Access>
                        </Popconfirm>
                    )}
                    <Access rules={TextContentRules.EDIT_NAMINGS}>
                        <Button
                            type='primary'
                            htmlType='submit'
                            loading={isLoading}
                            style={{ marginLeft: 'auto' }}
                            data-test='rubric-save-button'
                        >
                            {isEditing ? 'Save' : 'Create'}
                        </Button>
                    </Access>
                </Flex>
            </Form.Item>
        </Form>
    )
}

export default RubricPropertiesTab
